/** O que cada formato da quiz permite no formulário.
 *
 * Espelha o backend: ``QuizConfig`` para a trivia e ``CHOICE_DEFAULTS`` /
 * ``CHOICE_LIMITS`` (``src/models/quiz.py``) para a Escolha. Se os dois
 * divergirem, o form aceita o que o backend recusa com 422 — mantenha juntos.
 * Plano: ``apps/backend/docs/plan-quiz-choice.md``.
 */
import type { QuizVideoType } from '@/api/endpoints/factories'

export type SecondsKey = '3' | '4' | '5' | '6' | '7' | '10'

export interface FormatRules {
  /** Namespace dos temas em ``config/themes/``. */
  themePrefix: string
  rounds: readonly [number, number]
  options: ReadonlyArray<'2' | '3' | '4'>
  /** Botões mostrados no seletor de tempo. */
  seconds: ReadonlyArray<SecondsKey>
  /** O que a validação aceita — mais largo que os botões: um preset de trivia
   * salvo com 4 s ou 8 s continua válido, como era antes e como o backend aceita. */
  secondsRange: readonly [number, number]
  defaults: { num_questions: number; options_per_question: number; seconds_per_question: number }
  allowBothLanguages: boolean
  hasDifficulty: boolean
  /** Rótulo do "Aleatório": na Escolha o sorteio inclui um prompt novo,
   * gerado na hora (``src/factories/quiz/theme_pick.py``). */
  randomLabel: string
  roundsLabel: string
  secondsLabel: string
  kicker: string
  title: string
  subtitle: string
  promptPlaceholder: string
}

export const FORMAT_RULES: Readonly<Record<QuizVideoType, FormatRules>> = {
  trivia: {
    themePrefix: 'quiz-',
    rounds: [2, 10],
    options: ['2', '3', '4'],
    seconds: ['3', '5', '7', '10'],
    secondsRange: [3, 10],
    defaults: { num_questions: 5, options_per_question: 4, seconds_per_question: 5 },
    allowBothLanguages: true,
    hasDifficulty: true,
    randomLabel: 'Aleatório',
    roundsLabel: 'Perguntas',
    secondsLabel: 'Tempo por pergunta',
    kicker: 'Quiz · perguntas e respostas',
    title: 'Que tipo de quiz vamos jogar?',
    subtitle:
      'Escolha um tema; o Prism gera N perguntas de múltipla escolha com countdown e revela cada resposta.',
    promptPlaceholder: 'Ex: 5 perguntas sobre filmes vencedores do Oscar nos anos 90',
  },
  choice: {
    themePrefix: 'choice-',
    rounds: [3, 5],
    options: ['2', '3'],
    seconds: ['3', '4', '5', '6'],
    secondsRange: [3, 6],
    defaults: { num_questions: 3, options_per_question: 2, seconds_per_question: 3 },
    allowBothLanguages: false,
    hasDifficulty: false,
    randomLabel: 'Aleatório (tema ou prompt novo)',
    roundsLabel: 'Rodadas',
    secondsLabel: 'Tempo por rodada',
    kicker: 'Escolha · sem resposta certa',
    title: 'Qual dilema vamos lançar?',
    subtitle:
      'Escolha um tema; o Prism gera dilemas rápidos sem resposta certa e fecha pedindo a sequência de letras nos comentários.',
    promptPlaceholder: 'Ex: dilemas sobre viagens de férias e aeroportos',
  },
}

export function formatDefaults(videoType: QuizVideoType): FormatRules['defaults'] {
  return { ...FORMAT_RULES[videoType].defaults }
}

export function secondsLabel(seconds: SecondsKey): string {
  return `${seconds}s`
}
